// biome-ignore-all lint/suspicious/noExplicitAny: <intentionally any>
import { useWindowSize } from '@vueuse/core';
import { computed, type ComputedRef, getCurrentInstance, useSlots } from 'vue';
import type {
  ClickRowArgument,
  Column,
  DataTableProps,
} from '../components/DataTable/DataTable.types';

type DatatableEmits = {
  (evt: 'rowClick', row: ClickRowArgument<Record<string, any>>): void;
  (evt: 'middleclickRow', row: ClickRowArgument<Record<string, any>>): void;
};

export type DataTableInternalsProps = {
  tableMinHeight: number;
  tableHeight: DataTableProps['height'];
  emits: DatatableEmits;
  headers: () => Column[];
  selectable: () => boolean;
  clickableRows: () => boolean;
};

export type DataTableInternals = {
  tableCustomHeight: ComputedRef<number>;
  itemsSlotsNames: ComputedRef<string[]>;
  onRowClick: (row: any) => any;
  onMiddleclickRow: (row: any) => any;
  rowIsClickable: ComputedRef<boolean>;
  rowsAreInteractive: ComputedRef<boolean>;
  resetTableScroll: () => void;
};

export function useDataTableInternals(
  props: DataTableInternalsProps,
): DataTableInternals {
  const slots = useSlots();

  const ITEM_SLOT_PREFIX = 'item-';
  const EXPANDED_ITEM_SLOT_PREFIX = 'expand';
  const ACTIONS_COLUMN_VALUE = 'actions';
  const ACTIONS_ITEM_SLOT_NAME = 'item-actions';

  const itemsSlotsNames = computed(() => {
    return Object.keys(slots).filter((slotName) => {
      return (
        slotName.startsWith(ITEM_SLOT_PREFIX) ||
        slotName.startsWith(EXPANDED_ITEM_SLOT_PREFIX)
      );
    });
  });

  const { height: windowHeight } = useWindowSize();
  const finalTableHeight = computed(() => {
    return Math.max(
      props.tableHeight ?? windowHeight.value * 0.6,
      props.tableMinHeight,
    );
  });

  const onRowClick = (rowData: ClickRowArgument<Record<string, any>>): any => {
    props.emits('rowClick', rowData);
  };

  const onMiddleclickRow = (
    rowData: ClickRowArgument<Record<string, any>>,
  ): any => {
    props.emits('middleclickRow', rowData);
  };

  const rowIsClickable = computed<boolean>(() => {
    const hasClickEventListener = computed(
      () => !!getCurrentInstance()?.vnode.props?.onRowClick,
    );
    return hasClickEventListener.value;
  });

  const hasActionsColumn = computed(() => {
    return (
      props.headers().some((header) => header.value === ACTIONS_COLUMN_VALUE) ||
      ACTIONS_ITEM_SLOT_NAME in slots
    );
  });

  const hasExpandableRows = computed(() => {
    return Object.keys(slots).some((slotName) =>
      slotName.startsWith(EXPANDED_ITEM_SLOT_PREFIX),
    );
  });

  const rowsAreInteractive = computed<boolean>(() => {
    return (
      rowIsClickable.value ||
      props.clickableRows() ||
      props.selectable() ||
      hasActionsColumn.value ||
      hasExpandableRows.value
    );
  });

  const resetTableScroll = () => {
    const tabelHtmlEl = document.querySelector('.internal-data-table__main');
    // reset scroll position
    if (tabelHtmlEl) {
      tabelHtmlEl.scrollTop = 0;
    }
  };

  return {
    tableCustomHeight: finalTableHeight,
    itemsSlotsNames,
    onRowClick,
    onMiddleclickRow,
    rowIsClickable,
    rowsAreInteractive,
    resetTableScroll,
  };
}
